import React, { useState, useEffect } from 'react'
import { Tag, Clock, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import usePaymentStore from '../store/usePaymentStore'

/**
 * PromoBanner — баннер активного промокода на странице пополнения
 * Показывает информацию о лучшем активном промокоде (bestPromo)
 * и кнопку для ввода нового промокода
 */
const PromoBanner = () => {
  const { t } = useTranslation()
  const { bestPromo, setPromoInputOpen } = usePaymentStore()
  const [minutesLeft, setMinutesLeft] = useState(null)

  // Таймер обратного отсчёта для промокода с ограничением по времени
  useEffect(() => {
    if (!bestPromo?.minutes_left) {
      setMinutesLeft(null)
      return
    }

    setMinutesLeft(bestPromo.minutes_left)

    const interval = setInterval(() => {
      setMinutesLeft((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 60000)

    return () => clearInterval(interval)
  }, [bestPromo])

  // Общий рендер таймера
  const renderTimer = (size = 12) => {
    if (minutesLeft !== null && minutesLeft > 0) {
      return (
        <span className="flex items-center gap-1 text-yellow-400 text-xs">
          <Clock size={size} />
          {t('promo.minutesLeft', { minutes: minutesLeft })}
        </span>
      )
    }
    if (bestPromo?.minutes_left === null) {
      return (
        <span className="text-text-secondary text-xs">
          {t('promo.timeless')}
        </span>
      )
    }
    return null
  }

  // Кнопка "Ввести промокод" — единая для всех вариантов
  const renderEnterButton = () => (
    <button
      onClick={() => setPromoInputOpen(true)}
      className="w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 flex items-center justify-center gap-2 text-white/70 hover:text-white hover:border-white/20 hover:bg-white/10 transition-all"
    >
      <Tag size={18} />
      <span className="font-medium text-sm">{t('promo.buttonEnter')}</span>
    </button>
  )

  // Если нет активного промокода — только кнопка ввода
  if (!bestPromo) {
    return (
      <div className="mb-4">
        {renderEnterButton()}
      </div>
    )
  }

  return (
    <div className="mb-4">
      <div className="relative rounded-xl overflow-hidden mb-3">
        {/* Полный градиентный фон */}
        <div className="absolute inset-0 bg-gradient-to-r from-accent-primary/20 via-accent-primary/10 to-accent-secondary/15" />

        {/* Декоративная волна снизу */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-primary/50 to-transparent" />

        <div className="relative p-4">
          <div className="flex items-center gap-3">
            {/* Крупный процент слева */}
            <div className="flex items-baseline gap-1 flex-shrink-0">
              <span className="text-3xl font-black bg-gradient-to-br from-accent-primary to-accent-secondary bg-clip-text text-transparent leading-none">
                <bdi dir="ltr">+{bestPromo.percent}%</bdi>
              </span>
            </div>

            {/* Вертикальный разделитель */}
            <div className="w-px h-10 bg-white/10 flex-shrink-0" />

            {/* Информация */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Sparkles size={13} className="text-accent-primary flex-shrink-0" />
                <span className="text-white text-sm font-semibold">
                  {bestPromo.multi_deposit
                    ? t('promo.multiDeposit')
                    : t('promo.singleDeposit')}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {renderTimer()}
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderEnterButton()}
    </div>
  )
}

export default PromoBanner
